import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EmbeddedLectureProps {
  embedUrl: string;
  lectureId: string;
  title?: string;
  onSlideView?: (slideId: string, slideIndex: string) => void;
}

const EmbeddedLecture: React.FC<EmbeddedLectureProps> = ({ 
  embedUrl, 
  lectureId, 
  title,
  onSlideView 
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(600);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin if needed for security
      // if (event.origin !== 'https://your-trusted-domain.com') return;
      
      if (event.data.lectureId !== lectureId) return;
      
      switch (event.data.type) {
        case 'resize':
          if (event.data.height && iframeRef.current) {
            setIframeHeight(event.data.height + 20); // Add some padding
          }
          break;
          
        case 'slideViewed':
          if (onSlideView && event.data.slideId && event.data.slideIndex) {
            onSlideView(event.data.slideId, event.data.slideIndex);
          }
          break;
          
        case 'ready':
          setIsLoading(false);
          break;
          
        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [lectureId, onSlideView]);

  const handleIframeLoad = () => {
    // Set a timeout in case the iframe doesn't send a ready message
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  const handleIframeError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  if (hasError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load lecture content. Please try refreshing the page.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 bg-white/80 flex items-center justify-center">
          <div className="space-y-4 w-full max-w-2xl px-4">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      )}
      
      <Card className="overflow-hidden border-0 shadow-none">
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title={title || `Lecture ${lectureId}`}
          className="w-full border-0 transition-all duration-300"
          style={{ 
            height: `${iframeHeight}px`,
            minHeight: '600px',
            opacity: isLoading ? 0 : 1
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-scripts allow-same-origin"
        />
      </Card>
    </div>
  );
};

export default EmbeddedLecture; 